'use client';

import type { BacktestConfigValues } from '../components/BacktestWorkspace';

interface KPI {
  label: string;
  value: string;
  sub: string;
}

interface BtTrade {
  id: string;
  date: string;
  symbol: string;
  direction: 'Long' | 'Short';
  entry: number;
  exit: number;
  pnl: number;
  rr: number;
  patternScore: number;
  bars: number;
  outcome: 'Win' | 'Loss';
}

const equityData = [
  { month: 'Jan 25', equity: 25000 },
  { month: 'Feb 25', equity: 26840 },
  { month: 'Mar 25', equity: 24210 },
  { month: 'Apr 25', equity: 25980 },
  { month: 'May 25', equity: 28640 },
  { month: 'Jun 25', equity: 31200 },
  { month: 'Jul 25', equity: 29840 },
  { month: 'Aug 25', equity: 33410 },
  { month: 'Sep 25', equity: 36820 },
  { month: 'Oct 25', equity: 38140 },
  { month: 'Nov 25', equity: 35690 },
  { month: 'Dec 25', equity: 39240 },
  { month: 'Jan 26', equity: 41820 },
  { month: 'Feb 26', equity: 44160 },
  { month: 'Mar 26', equity: 42380 },
  { month: 'Apr 26', equity: 45920 },
  { month: 'May 26', equity: 48170 },
  { month: 'Jun 26', equity: 46840 },
  { month: 'Jul 26', equity: 47390 },
  { month: 'Aug 26', equity: 48170 },
];

const btTrades: BtTrade[] = [
  { id: 'bt-001', date: 'Jan 8, 2025', symbol: 'NVDA', direction: 'Long', entry: 128.40, exit: 136.20, pnl: 1170, rr: 3.1, patternScore: 88, bars: 24, outcome: 'Win' },
  { id: 'bt-002', date: 'Jan 15, 2025', symbol: 'NVDA', direction: 'Long', entry: 132.80, exit: 129.40, pnl: -510, rr: -0.9, patternScore: 72, bars: 8, outcome: 'Loss' },
  { id: 'bt-003', date: 'Jan 24, 2025', symbol: 'NVDA', direction: 'Long', entry: 131.20, exit: 138.80, pnl: 1140, rr: 2.8, patternScore: 91, bars: 31, outcome: 'Win' },
  { id: 'bt-004', date: 'Feb 5, 2025', symbol: 'NVDA', direction: 'Short', entry: 142.60, exit: 136.40, pnl: 930, rr: 2.4, patternScore: 79, bars: 18, outcome: 'Win' },
  { id: 'bt-005', date: 'Feb 18, 2025', symbol: 'NVDA', direction: 'Long', entry: 138.20, exit: 133.80, pnl: -660, rr: -1.1, patternScore: 71, bars: 6, outcome: 'Loss' },
  { id: 'bt-006', date: 'Mar 3, 2025', symbol: 'NVDA', direction: 'Long', entry: 134.60, exit: 142.40, pnl: 1170, rr: 3.0, patternScore: 85, bars: 28, outcome: 'Win' },
  { id: 'bt-007', date: 'Mar 14, 2025', symbol: 'NVDA', direction: 'Long', entry: 139.80, exit: 136.20, pnl: -540, rr: -0.8, patternScore: 73, bars: 11, outcome: 'Loss' },
  { id: 'bt-008', date: 'Mar 28, 2025', symbol: 'NVDA', direction: 'Short', entry: 148.40, exit: 141.20, pnl: 1080, rr: 2.7, patternScore: 82, bars: 22, outcome: 'Win' },
  { id: 'bt-009', date: 'Apr 9, 2025', symbol: 'NVDA', direction: 'Long', entry: 143.60, exit: 151.80, pnl: 1230, rr: 3.2, patternScore: 93, bars: 35, outcome: 'Win' },
  { id: 'bt-010', date: 'Apr 22, 2025', symbol: 'NVDA', direction: 'Long', entry: 148.20, exit: 144.60, pnl: -540, rr: -0.9, patternScore: 70, bars: 9, outcome: 'Loss' },
];

const kpis: KPI[] = [
  { label: 'Win Rate', value: '61.4%', sub: '54W / 34L · 88 total trades' },
  { label: 'Profit Factor', value: '2.18', sub: 'Gross $42,810 / $19,640' },
  { label: 'Net Return', value: '+$23,170', sub: '$25,000 initial · +92.7%' },
  { label: 'Max Drawdown', value: '-$4,820', sub: '-19.3% · Mar 2025 drawdown period' },
];

// Color helpers
const COLOR_PRIMARY = [99, 102, 241] as const;   // indigo
const COLOR_POSITIVE = [34, 197, 94] as const;   // green
const COLOR_NEGATIVE = [239, 68, 68] as const;   // red
const COLOR_WARNING = [234, 179, 8] as const;    // yellow
const COLOR_BG = [15, 17, 26] as const;          // dark bg
const COLOR_CARD = [22, 25, 37] as const;        // card bg
const COLOR_BORDER = [40, 44, 60] as const;      // border
const COLOR_TEXT = [226, 232, 240] as const;     // foreground
const COLOR_MUTED = [100, 116, 139] as const;    // muted

export async function downloadBacktestPDF(params: BacktestConfigValues): Promise<void> {
  const { jsPDF } = await import('jspdf');

  const doc = new jsPDF({ orientation: 'portrait', unit: 'pt', format: 'a4' });
  const PW = doc.internal.pageSize.getWidth();   // 595
  const PH = doc.internal.pageSize.getHeight();  // 842
  const MARGIN = 32;
  const CONTENT_W = PW - MARGIN * 2;

  // ── Background ──────────────────────────────────────────────────────────────
  doc.setFillColor(...COLOR_BG);
  doc.rect(0, 0, PW, PH, 'F');

  let y = MARGIN;

  // ── Header bar ──────────────────────────────────────────────────────────────
  doc.setFillColor(...COLOR_PRIMARY);
  doc.roundedRect(MARGIN, y, CONTENT_W, 52, 6, 6, 'F');

  doc.setTextColor(255, 255, 255);
  doc.setFontSize(16);
  doc.setFont('helvetica', 'bold');
  doc.text('TradeEdge — Backtest Report', MARGIN + 16, y + 20);

  doc.setFontSize(9);
  doc.setFont('helvetica', 'normal');
  doc.setTextColor(200, 200, 255);
  doc.text(
    `${params.symbol} · ${params.timeframe} · ${params.startDate} – ${params.endDate}`,
    MARGIN + 16,
    y + 36,
  );

  const now = new Date();
  const dateStr = now.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  doc.text(`Generated ${dateStr}`, PW - MARGIN - 16, y + 36, { align: 'right' });

  y += 68;

  // ── Section label helper ─────────────────────────────────────────────────────
  const sectionLabel = (label: string) => {
    doc.setFontSize(8);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(...COLOR_MUTED);
    doc.text(label.toUpperCase(), MARGIN, y);
    y += 4;
    doc.setDrawColor(...COLOR_BORDER);
    doc.setLineWidth(0.5);
    doc.line(MARGIN, y, MARGIN + CONTENT_W, y);
    y += 12;
  };

  // ── KPI Cards ────────────────────────────────────────────────────────────────
  sectionLabel('Key Performance Indicators');

  const kpiW = (CONTENT_W - 12) / 4;
  const kpiH = 62;

  kpis.forEach((kpi, i) => {
    const x = MARGIN + i * (kpiW + 4);
    const isWarning = kpi.label === 'Max Drawdown';

    // Card bg
    doc.setFillColor(...COLOR_CARD);
    doc.roundedRect(x, y, kpiW, kpiH, 4, 4, 'F');

    // Left accent bar
    const accentColor = isWarning ? COLOR_WARNING : COLOR_PRIMARY;
    doc.setFillColor(...accentColor);
    doc.roundedRect(x, y, 3, kpiH, 2, 2, 'F');

    // Label
    doc.setFontSize(7.5);
    doc.setFont('helvetica', 'normal');
    doc.setTextColor(...COLOR_MUTED);
    doc.text(kpi.label, x + 10, y + 16);

    // Value
    doc.setFontSize(15);
    doc.setFont('helvetica', 'bold');
    const valueColor = isWarning
      ? COLOR_WARNING
      : kpi.value.startsWith('+')
      ? COLOR_POSITIVE
      : COLOR_TEXT;
    doc.setTextColor(...valueColor);
    doc.text(kpi.value, x + 10, y + 34);

    // Sub
    doc.setFontSize(7);
    doc.setFont('helvetica', 'normal');
    doc.setTextColor(...COLOR_MUTED);
    doc.text(kpi.sub, x + 10, y + 48, { maxWidth: kpiW - 14 });
  });

  y += kpiH + 20;

  // ── Equity Curve ─────────────────────────────────────────────────────────────
  sectionLabel('Equity Curve');

  const chartH = 110;
  const chartW = CONTENT_W;

  // Chart background
  doc.setFillColor(...COLOR_CARD);
  doc.roundedRect(MARGIN, y, chartW, chartH + 28, 4, 4, 'F');

  // Draw equity curve as SVG-style polyline
  const values = equityData.map((d) => d.equity);
  const minV = Math.min(...values);
  const maxV = Math.max(...values);
  const rangeV = maxV - minV || 1;
  const chartPadX = 12;
  const chartPadY = 10;
  const innerW = chartW - chartPadX * 2;
  const innerH = chartH - chartPadY * 2;

  // Grid lines
  doc.setDrawColor(...COLOR_BORDER);
  doc.setLineWidth(0.3);
  for (let g = 0; g <= 4; g++) {
    const gy = y + chartPadY + (g / 4) * innerH;
    doc.line(MARGIN + chartPadX, gy, MARGIN + chartPadX + innerW, gy);
    const gridVal = maxV - (g / 4) * rangeV;
    doc.setFontSize(6.5);
    doc.setTextColor(...COLOR_MUTED);
    doc.text(`$${(gridVal / 1000).toFixed(0)}k`, MARGIN + 2, gy + 2);
  }

  // Fill area under curve
  const pts = equityData.map((d, i) => ({
    x: MARGIN + chartPadX + (i / (equityData.length - 1)) * innerW,
    y: y + chartPadY + innerH - ((d.equity - minV) / rangeV) * innerH,
  }));

  // Gradient fill approximation (multiple horizontal strips)
  for (let strip = 0; strip < innerH; strip++) {
    const alpha = 0.12 * (1 - strip / innerH);
    if (alpha < 0.005) continue;
    const stripY = y + chartPadY + strip;
    // Find x range where curve is above this y
    const curveY = stripY;
    let startX = -1;
    let endX = -1;
    for (let pi = 0; pi < pts.length - 1; pi++) {
      const p1 = pts[pi];
      const p2 = pts[pi + 1];
      if (p1.y <= curveY && p2.y <= curveY) {
        if (startX < 0) startX = p1.x;
        endX = p2.x;
      }
    }
    if (startX >= 0 && endX > startX) {
      doc.setFillColor(99, 102, 241);
      doc.setGState(doc.GState({ opacity: alpha }));
      doc.rect(startX, stripY, endX - startX, 1, 'F');
    }
  }
  doc.setGState(doc.GState({ opacity: 1 }));

  // Curve line
  doc.setDrawColor(...COLOR_PRIMARY);
  doc.setLineWidth(1.5);
  for (let i = 0; i < pts.length - 1; i++) {
    doc.line(pts[i].x, pts[i].y, pts[i + 1].x, pts[i + 1].y);
  }

  // X-axis labels (every 4 months)
  doc.setFontSize(6.5);
  doc.setTextColor(...COLOR_MUTED);
  equityData.forEach((d, i) => {
    if (i % 4 === 0 || i === equityData.length - 1) {
      const px = MARGIN + chartPadX + (i / (equityData.length - 1)) * innerW;
      doc.text(d.month, px, y + chartH + 18, { align: 'center' });
    }
  });

  // Start / end equity labels
  doc.setFontSize(7);
  doc.setTextColor(...COLOR_POSITIVE);
  doc.setFont('helvetica', 'bold');
  doc.text('$25,000 → $48,170 (+92.7%)', MARGIN + chartPadX, y + chartH + 10);

  y += chartH + 36;

  // ── Trade List ───────────────────────────────────────────────────────────────
  sectionLabel('Simulated Trade List');

  // Table header
  const cols = [
    { label: 'Date', w: 68 },
    { label: 'Dir', w: 36 },
    { label: 'Entry', w: 52 },
    { label: 'Exit', w: 52 },
    { label: 'P&L', w: 56 },
    { label: 'R:R', w: 40 },
    { label: 'Match%', w: 44 },
    { label: 'Bars', w: 36 },
    { label: 'Result', w: 44 },
  ];

  const rowH = 18;
  const headerH = 20;

  // Header bg
  doc.setFillColor(30, 34, 50);
  doc.roundedRect(MARGIN, y, CONTENT_W, headerH, 3, 3, 'F');

  let cx = MARGIN + 6;
  doc.setFontSize(7.5);
  doc.setFont('helvetica', 'bold');
  doc.setTextColor(...COLOR_MUTED);
  cols.forEach((col) => {
    doc.text(col.label, cx, y + 13);
    cx += col.w;
  });
  y += headerH;

  // Rows
  btTrades.forEach((trade, idx) => {
    // Check if we need a new page
    if (y + rowH > PH - MARGIN) {
      doc.addPage();
      doc.setFillColor(...COLOR_BG);
      doc.rect(0, 0, PW, PH, 'F');
      y = MARGIN;
    }

    // Alternating row bg
    if (idx % 2 === 1) {
      doc.setFillColor(25, 28, 42);
      doc.rect(MARGIN, y, CONTENT_W, rowH, 'F');
    }

    cx = MARGIN + 6;
    doc.setFontSize(7.5);
    doc.setFont('helvetica', 'normal');

    // Date
    doc.setTextColor(...COLOR_MUTED);
    doc.text(trade.date, cx, y + 12);
    cx += cols[0].w;

    // Direction badge
    const dirColor = trade.direction === 'Long' ? COLOR_POSITIVE : COLOR_NEGATIVE;
    doc.setFillColor(...dirColor);
    doc.setGState(doc.GState({ opacity: 0.15 }));
    doc.roundedRect(cx, y + 4, 28, 11, 2, 2, 'F');
    doc.setGState(doc.GState({ opacity: 1 }));
    doc.setTextColor(...dirColor);
    doc.setFont('helvetica', 'bold');
    doc.text(trade.direction, cx + 4, y + 12);
    cx += cols[1].w;

    // Entry / Exit
    doc.setFont('helvetica', 'normal');
    doc.setTextColor(...COLOR_TEXT);
    doc.text(`$${trade.entry.toFixed(2)}`, cx, y + 12);
    cx += cols[2].w;
    doc.text(`$${trade.exit.toFixed(2)}`, cx, y + 12);
    cx += cols[3].w;

    // P&L
    const pnlColor = trade.pnl >= 0 ? COLOR_POSITIVE : COLOR_NEGATIVE;
    doc.setTextColor(...pnlColor);
    doc.setFont('helvetica', 'bold');
    doc.text(`${trade.pnl >= 0 ? '+' : ''}$${trade.pnl.toLocaleString()}`, cx, y + 12);
    cx += cols[4].w;

    // R:R
    const rrColor = trade.rr >= 1.5 ? COLOR_POSITIVE : trade.rr >= 0 ? COLOR_WARNING : COLOR_NEGATIVE;
    doc.setTextColor(...rrColor);
    doc.setFont('helvetica', 'normal');
    doc.text(trade.rr >= 0 ? `1:${trade.rr.toFixed(1)}` : trade.rr.toFixed(1), cx, y + 12);
    cx += cols[5].w;

    // Pattern score
    doc.setTextColor(...COLOR_TEXT);
    doc.text(`${trade.patternScore}%`, cx, y + 12);
    cx += cols[6].w;

    // Bars
    doc.setTextColor(...COLOR_MUTED);
    doc.text(`${trade.bars}`, cx, y + 12);
    cx += cols[7].w;

    // Outcome badge
    const outcomeColor = trade.outcome === 'Win' ? COLOR_POSITIVE : COLOR_NEGATIVE;
    doc.setFillColor(...outcomeColor);
    doc.setGState(doc.GState({ opacity: 0.15 }));
    doc.roundedRect(cx, y + 4, 32, 11, 2, 2, 'F');
    doc.setGState(doc.GState({ opacity: 1 }));
    doc.setTextColor(...outcomeColor);
    doc.setFont('helvetica', 'bold');
    doc.text(trade.outcome, cx + 4, y + 12);

    y += rowH;
  });

  // ── Footer ───────────────────────────────────────────────────────────────────
  const totalPages = doc.getNumberOfPages();
  for (let p = 1; p <= totalPages; p++) {
    doc.setPage(p);
    doc.setFillColor(20, 22, 34);
    doc.rect(0, PH - 28, PW, 28, 'F');
    doc.setFontSize(7);
    doc.setFont('helvetica', 'normal');
    doc.setTextColor(...COLOR_MUTED);
    doc.text('TradeEdge · Backtest Report · For informational purposes only', MARGIN, PH - 10);
    doc.text(`Page ${p} of ${totalPages}`, PW - MARGIN, PH - 10, { align: 'right' });
  }

  // ── Save ─────────────────────────────────────────────────────────────────────
  const filename = `backtest-${params.symbol}-${params.timeframe}-${dateStr.replace(/\s/g, '-')}.pdf`;
  doc.save(filename);
}
